import { useContext } from 'react';
import { Link as RouterLink } from 'react-router';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Box from '@mui/material/Box';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import DropdownButton from '#components/DropdownButton';

import {
  useRequestSubscriptionMutation,
} from '#services/marketplace';

import {
  PRICE_MODEL,
  BILLING_CYCLE,
  SUBSCRIPTION_ACTION,
} from '#common/constants/addons';
import {
  formatDate,
} from '#common/utils';

import defaultIcon from '#addons/assets/addon.png';

interface AddonPrice {
  cycle: string;
  price: number;
}

interface AddonInfo {
  id: number | string;
  name: string;
  version: string;
  icon?: string;
  descriptionText?: string;
  descriptionComponent?: React.ReactNode;
  priceModel: string;
  price?: number;
  prices: AddonPrice[];
}

interface Addon {
  info: AddonInfo;
  enabled?: boolean;
  expired?: boolean;
  expiredAt?: string | number | Date;
  unsubscribed?: boolean;
  billingCycle?: string;
}

interface RenderAddonDetailArgs {
  addon: Addon;
  idx?: number;
  details?: boolean;
}

interface RequestSubscriptionArgs {
  addonId: number | string;
  action: string;
  requestBillingCycle?: string | number;
}

const renderAddonDetail = ({ addon, idx, details }: RenderAddonDetailArgs) => {
  const {
    info,
    enabled,
    expired,
    expiredAt,
    unsubscribed,
    billingCycle,
  } = addon;

  const [requestSubscription] = useRequestSubscriptionMutation();
  const {
    showBackdrop,
    hideBackdrop,
    showDialog,
    hideDialog,
    setDialogData,
  } = useContext(FeedbackUIContext)!;

  const requestSubscriptionHandler = ({
    addonId,
    action,
    requestBillingCycle,
  }: RequestSubscriptionArgs) => {

    const dialogBody = (action === SUBSCRIPTION_ACTION.APPLY)
      ? 'Are you sure to request the installation of the app? You\'ll be charged through the payment method on file.'
      : (action === SUBSCRIPTION_ACTION.CHANGE_PLAN)
        ? `Are you sure to subscribe to the ${requestBillingCycle} plan? Changes will be applied to your next billing cycle.`
        : 'Are you sure to request the uninstallation of the app?\n\nBy unsubscribing from the current add-on, you will no longer have access to the features it provides.';

    setDialogData({
      title: 'Confirmation',
      text: dialogBody,
      disagree: 'No',
      agree: 'Yes',
      onDisagree: () => hideDialog(),
      onAgree: () => {
        hideDialog();
        showBackdrop();

        void requestSubscription({
          expiredAt: formatDate(expiredAt as string | number | Date),
          addonId,
          action,
          billingCycle: requestBillingCycle,
        }).then((res) => {
          const resText = res.error
            ? 'Failed to send the request, please contact the admin'
            : 'The request has been sent to the SWM team. We will process your request within 24 hours.';

          setDialogData({
            title: 'Confirmation',
            text: resText,
            agree: 'OK',
            onAgree: () => hideDialog(),
          });
          showDialog();
        }).finally(() => {
          hideBackdrop();
        });
      },
    });
    showDialog();
  };

  const renderActionButton = () => {
    if (enabled
      && !expired
      && [PRICE_MODEL.FREE, PRICE_MODEL.ONE_TIME_PURCHASE].includes(info.priceModel)
    ) {
      return (
        <Button
          variant="contained"
          disabled
        >
          Activated
        </Button>
      );
    }

    if (
      enabled
      && !unsubscribed
      && !expired
      && info.priceModel === PRICE_MODEL.CONTINUES_SUBSCRIPTION
    ) {
      return (
        <DropdownButton
          label="Change Plan"
          options={info.prices.map((p) => ({
            value: p.cycle,
            label: `Change to (${p.cycle})`,
            selected: (billingCycle === p.cycle),
            disabled: (billingCycle === p.cycle),
          }))}
          onClick={(e, val) => requestSubscriptionHandler({
            addonId: info.id,
            action: SUBSCRIPTION_ACTION.CHANGE_PLAN,
            requestBillingCycle: val,
          })}
        />
      );
    }

    if (info.priceModel === PRICE_MODEL.CONTINUES_SUBSCRIPTION) {
      return (
        <DropdownButton
          label="Select A Plan"
          options={info.prices.map((p) => ({
            value: p.cycle,
            label: `Request to Install (${p.cycle})`,
          }))}
          onClick={(e, val) => requestSubscriptionHandler({
            addonId: info.id,
            action: SUBSCRIPTION_ACTION.APPLY,
            requestBillingCycle: val,
          })}
        />
      );
    }

    return (
      <Button
        variant="contained"
        onClick={() => requestSubscriptionHandler({
          addonId: info.id,
          action: SUBSCRIPTION_ACTION.APPLY,
        })}
      >
        Request to Install
      </Button>
    );
  };

  const renderCancelButton = () => {
    if (!enabled || expired || unsubscribed) {
      return null;
    }

    return (
      <Button
        onClick={() => requestSubscriptionHandler({
          addonId: info.id,
          action: SUBSCRIPTION_ACTION.CANCEL,
        })}
      >
        Cancel subscription
      </Button>
    );
  };

  const renderExpireInfo = () => {
    if (info.priceModel !== PRICE_MODEL.CONTINUES_SUBSCRIPTION) {
      return null;
    }

    let expiringText = '';

    if (enabled) {
      if (expired) {
        expiringText = `Expired on: ${formatDate(expiredAt as string | number | Date)}`;
      } else if (unsubscribed) {
        expiringText = `Available until ${formatDate(expiredAt as string | number | Date)}`;
      } else {
        expiringText = `Next Billing Date: ${formatDate(expiredAt as string | number | Date)}`;
      }
    }

    return (
      <Typography
        variant={'string' as TypographyProps['variant']}
        sx={{
          color: "#BA000D",
          fontStyle: 'italic',
          fontSize: 14
        }}>
        {expiringText}
      </Typography>
    );
  };

  const renderPrices = () => {
    const { priceModel, price, prices } = info;

    if (priceModel === PRICE_MODEL.FREE) {
      return 'Free';
    }

    if (priceModel === PRICE_MODEL.ONE_TIME_PURCHASE) {
      return `One time fee $${price}`;
    }

    return prices.map((p) => {
      if (p.cycle === BILLING_CYCLE.MONTHLY) {
        return `$${p.price}/month`;
      }
      if (p.cycle === BILLING_CYCLE.ANNUALLY) {
        return `$${p.price}/year`;
      }

      throw new Error('Unknown price cycle');
    }).join(', ');
  };

  return (
    <Card key={idx}>
      <Box sx={{ display: 'flex', p: 2 }}>
        <CardMedia
          component="img"
          sx={{ width: 64, height: 64 }}
          image={info.icon || defaultIcon}
        />
        <CardContent sx={{ py: 0, '&:last-child': { pb: 0 } }}>
          <Typography variant="button" sx={{ fontSize: 18, mr: 2 }} gutterBottom>
            <Link component={RouterLink} to={`/management/marketplace/detail?addonId=${info.id}`}>{info.name}</Link>
            <Typography variant="caption" sx={{ ml: 1, fontStyle: 'italic', textTransform: 'capitalize' }}>
              ver.
              {info.version}
            </Typography>
          </Typography>
          {renderExpireInfo()}
          <Typography
            gutterBottom
            sx={{
              color: "text.secondary",
              fontSize: 14
            }}>
            {info.descriptionText}
          </Typography>
        </CardContent>
      </Box>
      {details && info.descriptionComponent && (
        <Box sx={{ p: 2 }}>
          {details && info.descriptionComponent}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          px: 2,
        }}
      >
        <Typography variant="subtitle1" gutterBottom sx={{
          fontWeight: "bold"
        }}>
          Pricing
        </Typography>
        <Box>
          {renderActionButton()}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'end',
          px: 2,
          pb: 2,
        }}
      >
        <Typography
          gutterBottom
          sx={{
            color: "#00848e",
            fontSize: 14,
            fontWeight: 'bold'
          }}>
          {renderPrices()}
        </Typography>
        <Box>
          {renderCancelButton()}
        </Box>
      </Box>
    </Card>
  );
};

export {

  renderAddonDetail,
};
export type { Addon };
